import { useState } from 'react'
import { Link } from 'react-router-dom'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL
      const response = await fetch(`${backendUrl}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess(true)
        setEmail('')
      } else {
        // Still show success message for security (don't reveal if email exists)
        setSuccess(true)
      }
    } catch (err) {
      console.error('Password reset request error:', err)
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="fixed inset-0 w-full h-full object-cover"
        style={{ zIndex: 0, filter: 'brightness(0.7)' }}
      >
        <source src="/assets/world-leaders-nuke.mp4" type="video/mp4" />
      </video>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" style={{ zIndex: 1 }} />
      
      {/* Content */}
      <div 
        className="relative bg-slate-900/95 backdrop-blur-md p-8 rounded-lg w-full max-w-md border-4 border-red-500/50 shadow-2xl"
        style={{
          zIndex: 2,
          boxShadow: '0 0 40px rgba(239, 68, 68, 0.4), 0 0 80px rgba(239, 68, 68, 0.2), inset 0 2px 20px rgba(0,0,0,0.5)'
        }}
      >
        {/* Title */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">🔐</div>
          <h1 className="text-3xl font-bold font-mono tracking-wider mb-2"
            style={{
              background: 'linear-gradient(to right, #ef4444, #dc2626)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 20px rgba(239, 68, 68, 0.5)',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.9))',
            }}
          >
            Password Reset
          </h1>
          <p className="text-slate-300 text-sm tracking-wide">
            Recover Your Commander Access
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-500/20 border-2 border-green-400 rounded-lg p-4">
            <div className="text-center">
              <div className="text-4xl mb-2">✅</div>
              <h3 className="text-lg font-bold text-green-300 mb-2">Check Your Email!</h3>
              <p className="text-slate-300 text-sm">
                If an account exists with this email, you'll receive password reset instructions shortly.
              </p>
              <p className="text-slate-400 text-xs mt-2">
                Check your spam folder if you don't see it within a few minutes.
              </p>
            </div>
          </div>
        )}

        {/* Form or Instructions */}
        {!success ? (
          <>
            <div className="bg-red-500/10 border-2 border-red-400/40 rounded-lg p-4 mb-6">
              <p className="text-slate-300 text-sm text-center">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2 tracking-wide">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-slate-800/80 border-2 border-slate-700 rounded-lg focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/50 text-white placeholder-slate-500 transition-all font-mono"
                  style={{
                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)'
                  }}
                  placeholder="commander@nation.com"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white font-bold py-4 px-4 rounded-lg hover:from-red-500 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider text-lg border-2 border-red-400/50 relative overflow-hidden group"
                style={{
                  boxShadow: '0 0 20px rgba(239, 68, 68, 0.5), 0 4px 12px rgba(0,0,0,0.5)',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                <span className="relative z-10">
                  {loading ? '🔄 Sending...' : '📧 Send Reset Link'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-red-500 opacity-0 group-hover:opacity-30 transition-opacity" />
              </button>
            </form>
          </>
        ) : (
          <div className="text-center">
            <Link
              to="/login"
              className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-8 rounded-lg hover:from-blue-500 hover:to-purple-500 transition-all uppercase tracking-wider border-2 border-blue-400/50"
              style={{
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)'
              }}
            >
              ← Back to Login
            </Link>
          </div>
        )}

        {/* Back to Login Link */}
        {!success && (
          <div className="mt-6 text-center">
            <p className="text-slate-300 text-sm">
              Remember your password?{' '}
              <Link 
                to="/login" 
                className="text-blue-400 hover:text-blue-300 font-bold tracking-wide transition-colors"
                style={{ textShadow: '0 0 10px rgba(59, 130, 246, 0.5)' }}
              >
                Back to Login
              </Link>
            </p>
          </div>
        )}
      </div>
      
      {/* Animated glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
    </div>
  )
}
